import dayjs from "dayjs";
import React, { FC, useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { addDays, format } from "date-fns";
import { debounce, get } from "lodash";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import ProofDocumentControl from "~/components/Forms/ViolationRecordingForm/ProofDocumentControl";
import { NOTIFICATIONS } from "~/constans/notifications";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { onScrollBottomViolationTypes } from "~/hooks/selectViolationTypeValue";
import { useGetUsersForSelect } from "~/hooks/useGetUsersForSelect";
import { dateDifIgnoreWeekends } from "~/libs/dateDifIgnoreWeekends";
import { language } from "~/libs/getData";
import { getFormatDate } from "~/libs/getFormatDate";
import { getCloseWeekday } from "~/libs/getWeekday";
import { isWeekend } from "~/libs/isWeekend";
import { uploadFile } from "~/libs/uploadFile";
import {
  Dealer,
  DealerStatus,
  useAddNotificationMutation,
  useCreateViolationMutation,
  useEditViolationMutation,
  useGetMyDealersLazyQuery,
  useGetProfileQuery,
} from "~/schemas/generated/graphql";
import { Field } from "~/shared/ui/Field";

import s from "./ViolationRecordingForm.less";

interface IProps {
  personalId: string;
  violationInstance?: any;
}

/** Догрузка опций при скролле выпадающего списка antd-select до низа. */
const handlePopupScroll =
  (onBottom: () => void) => (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 4) {
      onBottom();
    }
  };

const ViolationRecordingForm: FC<IProps> = ({
  personalId,
  violationInstance,
}) => {
  const { t } = useTranslation();
  const setData = useSetLocationData();
  const isEn = language === "en";
  const { fileId, page } = useGetLocationData();
  const history = useHistory();
  const [addNotificationMutation] = useAddNotificationMutation({});
  const [dealersError, setDealersError] = useState(false);
  const [uploadFileLoader, setUploadFileLoader] = useState(false);
  const isEdit = Object.keys(violationInstance).length > 1;

  const schema = yup.object().shape({
    violationType: yup.object().shape({
      value: yup
        .number()
        .min(1, t("validation.noDefaultSelection"))
        .required(t("validation.required")),
    }),
    description: yup
      .string()
      .test(
        "description",
        t("validation.required"),
        () => !watch("description") || !!watch("description").trim()
      )
      .max(1000, t("validation.more1000"))
      .nullable()
      .required(t("validation.required")),
    dateTo: yup.string().nullable().required(t("validation.required")),
    DocNum: yup.string().min(1).nullable().required(t("validation.required")),
    DocDate: yup.date().nullable().required(t("validation.required")),
    dealerSelect: isEdit
      ? null
      : yup
          .array()
          .of(
            yup.object().shape({
              label: yup.string().required(t("validation.required")),
              value: yup.string().required(t("validation.required")),
            })
          )
          .min(1, t("validation.required"))
          .required(t("validation.required")),
  });

  type notifType = "add" | "edit";

  const changeApply = (type: notifType) => {
    const notifStr =
      (type === "add" && "common.adding_was_successful") ||
      (type === "edit" && "common.сhange_was_successful");
    addNotificationMutation({
      variables: {
        type: NOTIFICATIONS.success,
        content: t(notifStr),
      },
    });
  };

  useEffect(() => {
    if (violationInstance.file) {
      setData("fileId", violationInstance.file);
    }
  }, []);

  const [getMyDealersQuery, { data, loading, fetchMore }] =
    useGetMyDealersLazyQuery({
      variables: {
        page: 0,
        count: 25,
        partName: null,
        statuses: DealerStatus.Active,
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    getMyDealersQuery();
  }, []);

  const dealershipsForSelect = (
    get(data, "DealerGetter.getMyDealers.items", []) as Dealer[]
  ).map(({ id, DealerName }) => ({
    label: DealerName,
    value: id,
  }));

  const onScrollBottomDealers = () => {
    fetchMore({
      variables: {
        page: Math.ceil(dealershipsForSelect.length / 25),
      },
      updateQuery: ({ DealerGetter }, { fetchMoreResult }) => {
        if (!fetchMoreResult) return { DealerGetter };
        return {
          DealerGetter: {
            ...DealerGetter,
            getMyDealers: {
              ...DealerGetter.getMyDealers,
              items: [
                ...DealerGetter.getMyDealers.items,
                ...get(fetchMoreResult, "DealerGetter.getMyDealers.items", []),
              ],
            },
          },
        };
      },
    });
  };

  const searсhDealer = debounce((partName = null) => {
    getMyDealersQuery({
      variables: {
        page: 0,
        partName,
      },
    });
  });

  const { handleSubmit, control, watch, errors, setValue } = useForm({
    defaultValues: {
      ...violationInstance,
      DocNum: !!violationInstance.DocNum ? violationInstance.DocNum : undefined,
      DocDate: !!violationInstance.DocDate
        ? new Date(violationInstance.DocDate)
        : undefined,
      dateFrom: isEdit
        ? violationInstance.dateFrom
        : getFormatDate(violationInstance.dateFrom, true),

      responsibleChecker: violationInstance.responsibleByUser
        ? {
            value: violationInstance.responsibleByUser.id,
            label: isEn
              ? violationInstance.responsibleByUser.fullnameEn
              : violationInstance.responsibleByUser.fullname,
          }
        : { value: "", label: t("violationRecording.selector") },
      dealerSelect: [],
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (!isEdit && watch("dealerSelect").length > 0) {
      setDealersError(false);
    }
  }, [watch]);

  const {
    violationTypesForSelect,
    introspectionHandler,
    searchType,
    getAvgCorrectionTime,
  } = onScrollBottomViolationTypes(true);

  const [createViolationMutation, createViolationState] =
    useCreateViolationMutation({
      onCompleted: () => {
        changeApply("add");
        history.push("/violations/");
        page && setData("page", page);
      },
    });
  const [editViolationMutation, editViolationState] = useEditViolationMutation({
    onCompleted: () => {
      changeApply("edit");
      history.push(`/violations/description/${personalId}`);
      page && setData("page", page);
    },
  });

  const creation = ({
    violationType,
    dateTo,
    description,
    responsibleChecker,
    DocDate,
    DocNum,
  }) => {
    if (watch("dealerSelect").length) {
      createViolationMutation({
        variables: {
          DealerIds: watch("dealerSelect").map((el) => el.value),
          ViolationTypesId: String(violationType.value),
          fixDeadline: format(new Date(dateTo), "yyyy-MM-dd'T'HH:mm:ssXXX"),
          DocDate: !!DocDate
            ? format(DocDate, "yyyy-MM-dd'T'HH:mm:ssXXX")
            : undefined,
          DocNum: DocNum,
          description,
          FileId: fileId || undefined,
          responsibleChecker: responsibleChecker.value || null,
        },
      });
    }
  };
  const editing = ({
    dateTo,
    description,
    responsibleChecker,
    DocDate,
    DocNum,
  }) => {
    editViolationMutation({
      variables: {
        id: violationInstance.id,
        fixDeadline: format(new Date(dateTo), "yyyy-MM-dd'T'HH:mm:ssXXX"),
        DocDate: !!DocDate
          ? format(DocDate, "yyyy-MM-dd'T'HH:mm:ssXXX")
          : undefined,
        DocNum: DocNum,
        description,
        DocId: fileId || undefined,
        responsibleChecker: responsibleChecker.value || null,
      },
    });
  };

  const onSubmit = (data) => {
    isEdit ? editing(data) : !dealersError && creation(data);
  };

  const checkDealers = () => {
    if (watch("dealerSelect").length <= 0) {
      setDealersError(true);
    }
  };

  const correctDate = (dateA, dateB) => {
    const a = new Date(dateA);
    const b = new Date(dateB);
    return dateDifIgnoreWeekends(a, b);
  };

  useEffect(() => {
    // @ts-ignore
    if (watch("violationType").value !== 0 && !violationInstance.dateTo) {
      //get avg
      const avgCorrectionDays = getAvgCorrectionTime(watch("violationType"));
      const createdAt = violationInstance.dateFrom;
      const createdAtCorrect = isWeekend(createdAt)
        ? getCloseWeekday(createdAt)
        : new Date(createdAt);
      //get shift date
      const shiftedDays = String(addDays(createdAtCorrect, avgCorrectionDays));
      //compute correction value with weekend
      const correctShiftDate =
        avgCorrectionDays - correctDate(createdAtCorrect, shiftedDays);
      //shift date with correction value
      const shiftCorrect = String(
        addDays(
          new Date(createdAtCorrect),
          avgCorrectionDays + correctShiftDate
        )
      );
      //check last date on weekday
      const checkWeekend = getCloseWeekday(shiftCorrect);
      //set in state
      setValue("dateTo", new Date(checkWeekend));
    } else {
      setValue("dateTo", violationInstance.dateTo || undefined);
    }
  }, [watch("violationType")]);

  const profileData = useGetProfileQuery().data;
  const { SubdivisionId, id } =
    get(profileData, "UserGetter.getProfile") || {};

  const usersForSelect = useGetUsersForSelect();

  const [searchText, setSearchText] = useState("");

  const filteredUsers = (usersArr) => {
    if (!searchText) {
      if (!!SubdivisionId) {
        const filteredValue = usersArr.filter((el) => {
          if (el.value !== id && el.SubdivisionId === SubdivisionId) {
            return el;
          }
        });
        return filteredValue.length > 0 ? filteredValue : usersArr;
      }
    }
    return usersArr;
  };

  const fUsers = filteredUsers(usersForSelect.users);

  const statusPlaceholder = { value: "1", label: t("violationsTable.toFix") };

  return (
    <form className={s.form} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        <Field
          type="select"
          name="violationType"
          control={control}
          label={t("violationRecording.violationType")}
          labelInValue
          options={violationTypesForSelect}
          showSearch
          filterOption={false}
          onSearch={(value: string) => searchType(value)}
          onPopupScroll={handlePopupScroll(introspectionHandler)}
          disabled={isEdit}
          defaultValue={
            violationInstance.violationType || {
              value: 0,
              label: t("violationRecording.selector"),
            }
          }
          error={errors["violationType"]?.value?.message}
        />

        <Field
          type="textarea"
          name="description"
          control={control}
          label={t("violationRecording.description")}
          placeholder={t("validation.more1000")}
          error={errors["description"]?.message}
        />

        {!isEdit && (
          <Field
            type="select"
            name="dealerSelect"
            control={control}
            label={t("users.allDC")}
            mode="multiple"
            labelInValue
            options={dealershipsForSelect}
            loading={loading}
            showSearch
            filterOption={false}
            onSearch={(value: string) => searсhDealer(value)}
            onPopupScroll={handlePopupScroll(onScrollBottomDealers)}
            error={errors["dealerSelect"]?.message}
          />
        )}

        <div>
          <span className={s.fieldLabel}>
            {t("violationRecording.proofDocument")}
          </span>
          {!!fileId ? (
            <ProofDocumentControl id={fileId} />
          ) : (
            <Field
              type="file"
              name="file"
              control={control}
              error={errors["file"]?.message}
              loading={uploadFileLoader}
              onUpload={async (file) => {
                setUploadFileLoader(true);
                try {
                  const uploaded = await uploadFile(file, () =>
                    addNotificationMutation({
                      variables: {
                        type: NOTIFICATIONS.error,
                        content: t("common.data_uploaded_error"),
                      },
                    })
                  );
                  if (uploaded) {
                    // Значение живёт в location state (fileId), а не в RHF:
                    // onUpload ничего не возвращает, поле остаётся пустым.
                    setData("fileId", uploaded.id);
                    addNotificationMutation({
                      variables: {
                        type: NOTIFICATIONS.success,
                        content: t("common.file_uploaded_successfully"),
                      },
                    });
                  }
                } finally {
                  setUploadFileLoader(false);
                }
              }}
            />
          )}
        </div>

        <Field
          type="input"
          name="dateFrom"
          control={control}
          label={t("violationRecording.dateFrom")}
          disabled
        />

        <Field
          type="datePicker"
          name="dateTo"
          control={control}
          label={t("violationRecording.dateTo")}
          format="DD.MM.YYYY"
          placement="topLeft"
          disabledDate={(current) =>
            current ? current.isBefore(dayjs(), "day") : false
          }
          error={errors["dateTo"]?.message}
        />

        <Field
          type="input"
          name="DocNum"
          control={control}
          label={t("violationRecording.DocNum")}
          error={errors["DocNum"]?.message}
        />

        <Field
          type="datePicker"
          name="DocDate"
          control={control}
          label={t("violationRecording.DocDate")}
          format="DD.MM.YYYY"
          placement="topLeft"
          error={errors["DocDate"]?.message}
        />

        {isEdit && (
          <Field
            type="input"
            name="user"
            control={control}
            label={t("violationRecording.user")}
            disabled
          />
        )}

        <Field
          type="select"
          name="responsibleChecker"
          control={control}
          label={t("violationRecording.executiveOfficer")}
          labelInValue
          options={[
            { value: "", label: t("violationRecording.selector") },
            ...fUsers,
          ]}
          showSearch
          filterOption={false}
          loading={usersForSelect.loading}
          onSearch={(value: string) => {
            setSearchText(value);
            usersForSelect.searchText(value);
          }}
          onPopupScroll={handlePopupScroll(usersForSelect.intersectionObserver)}
          error={errors["responsibleChecker"]?.message}
        />

        <Field
          type="select"
          name="status"
          control={control}
          label={t("violationRecording.status")}
          labelInValue
          options={[statusPlaceholder]}
          disabled
          defaultValue={statusPlaceholder}
        />

        <div className={s.submitRow}>
          <Button
            htmlType="submit"
            size={"medium"}
            onClick={checkDealers}
            loading={
              createViolationState.loading || editViolationState.loading
            }
          >
            {isEdit
              ? t("violationRecording.submitEdit")
              : t("violationRecording.submit")}
          </Button>
        </div>
      </Flex>
    </form>
  );
};
export default ViolationRecordingForm;
